import { ChangeDetectionStrategy, Component, inject, input, output, signal } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { noop } from "lodash";
import { BaseComponent } from "primeng/basecomponent";
import { SelectlistStyle } from "./selectlist.style";

/**
 * Selectlist is used to choose an item from a collection of options.
 *
 * Unlike other similar components, this one shows all available options in a more visual way,
 * thus making it easier for users to choose an item.
 *
 * @example
 * ```html
 * <ui-selectlist
 *   [options]="types"
 *   optionValue="value"
 *   optionLabel="label"
 *   optionDescription="description"
 *   formControlName="type"
 * />
 * ```
 */
@Component({
  selector: "ui-selectlist",
  templateUrl: "./selectlist.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: SelectlistComponent, multi: true }],
  host: { "[class]": "cx('root')" },
})
export class SelectlistComponent extends BaseComponent implements ControlValueAccessor {
  protected readonly _componentStyle = inject(SelectlistStyle);

  /**
   * An opaque collection of available options.
   */
  readonly options = input.required<unknown[]>();

  /**
   *
   * Name of the value field of an option.
   * @default undefined
   */
  readonly optionValue = input<string>();

  /**
   * Name of the label field of an option.
   * @default "label"
   */
  readonly optionLabel = input<string>("label");

  /**
   * Name of the test id attribute used for testing.
   * @default "testId"
   */
  readonly testId = input<string>("testId");

  /**
   *
   * Name of the optional description field of an option.
   * @default "description"
   */
  readonly optionDescription = input<string>("description");

  /**
   * Emits when value of the control changes.
   */
  readonly selected = output<unknown>();

  protected readonly disabled = signal<boolean>(false);
  protected readonly value = signal<unknown>(undefined);

  private changeCallback = noop;
  private touchCallback = noop;
  private touched = false;

  click(value: unknown): void {
    this.selected.emit(value);
    this.value.set(value);

    this.changeCallback(value);

    if (!this.touched) {
      this.touchCallback();
      this.touched = true;
    }
  }

  registerOnChange(callback: (_: unknown) => void): void {
    this.changeCallback = callback;
  }

  registerOnTouched(callback: () => void): void {
    this.touchCallback = callback;
  }

  writeValue(value: unknown): void {
    this.value.set(value);
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }
}
